import {Switch} from '@mantine/core';
import {StyleSheet, css} from 'aphrodite';
import React, {useState} from 'react';

import {getStepFromAnalysis} from 'analysis-tool/data/getStepFromAnalysis';
import refreshAnalysis from 'analysis-tool/refreshAnalysis';
import BenchlingConfig from 'BenchlingConfig';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import StepTypeMenu from 'pipelines/components/StepTypeMenu';
import {stepTypeIsConfigEditorSupported} from 'pipelines/generalized-step-config-editor/stepTypeIsConfigEditorSupported';
import {addStepWithInferredEdges} from 'pipelines/network/addStepWithInferredEdges';
import {PipelineStep, PipelineStepData} from 'pipelines/PipelineTypes';
import {getInputStepDatas, getLatestStepVersion, getPossibleEdges} from 'pipelines/StepConnectionUtils';
import PlainSearchInput, {BorderRadius, ClearType} from 'ui-library/PlainSearchInput';
import {withLoadingScreen} from 'util/Promises';

import {AnalysisRightSidebarItemType, RightSidebarComponentProps} from '../../RightSidebarTypes';
import RightSidebarSkeleton, {RightSidebarWidth} from '../RightSidebarSkeleton';

/** Step types that are dangerous to add, even though they are supported by the step config editor. */
const NON_ADDABLE_STEP_TYPES: Partial<Record<PipelineStepType, boolean>> = {
  // While CHART_VIEW is supported by the config editor, only certain charts types are supported.
  [PipelineStepType.CHART_VIEW]: true,
  // PARAMETER_OPTIMIZATION_CONFIGURATION steps cannot be removed once added.
  [PipelineStepType.PARAMETER_OPTIMIZATION_CONFIGURATION]: true,
  // DEFINE_TEMPLATE_VARIABLES steps are an experimental feature
  [PipelineStepType.DEFINE_TEMPLATE_VARIABLES]:
    !BenchlingConfig.ENABLE_ANALYSIS_TOOL_INTERACTIVE_FLOWCHART_EXPERIMENTAL,
};

/**
 * True if the step type is safe to and and edit in the generic way.
 */
function canAddStepType(stepType: PipelineStepType): boolean {
  if (stepType === PipelineStepType.JOIN || stepType === PipelineStepType.UNION_DATASETS) {
    return true;
  }

  // Don't allow adding steps which can't be configured in the generic way.
  return (
    stepTypeIsConfigEditorSupported(stepType, getLatestStepVersion(stepType)) &&
    !NON_ADDABLE_STEP_TYPES[stepType]
  );
}

/**
 * True if the step type can be connected to `step` and `stepData`.
 */
function canConnectStepTypeToStepData(
  step: PipelineStep,
  stepData: PipelineStepData,
  newStepType: PipelineStepType
): boolean {
  const possibleEdges = step.inputStepDatas.find(({id}) => id === stepData.id)
    ? getPossibleEdges({
        fromStepType: newStepType,
        toStepType: step.stepType,
        toStepData: {systemName: stepData.name},
      })
    : getPossibleEdges({
        toStepType: newStepType,
        fromStepType: step.stepType,
        fromStepData: {systemName: stepData.name},
      });

  // There may be more than one possible connection to the next step, such as to either the `Left` or `Right`
  // input of a join. In this scenario, the `addStep` util will choose a valid edge, and the user can
  // drag-and-drop edges to fix any incorrect connections.
  return possibleEdges.length > 0;
}

/**
 * True if the step type does not have any inputs.
 */
function isSourceStep(stepType: PipelineStepType): boolean {
  return getInputStepDatas(stepType).length === 0;
}

/**
 * Add a new step to the pipeline.
 *
 * If `selectedRightSidebarItem.stepId` and `selectedRightSidebarItem.stepDataName` are set, the new step
 * will be connected to the step data.
 *
 * Only step types can be edited from `AnalysisFlowchart` can be added.
 *
 */
export default function AddStepRightSidebar({
  analysis: pipeline,
  onChangeSelectedRightSidebarItem,
  selectedRightSidebarItem,
}: RightSidebarComponentProps<AnalysisRightSidebarItemType.ADD_STEP>): JSX.Element {
  const {stepId, stepDataName} = selectedRightSidebarItem;
  const [selectedStepType, setSelectedStepType] = useState<PipelineStepType | null>(null);
  const [showDescriptions, setShowDescriptions] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const step = stepId ? getStepFromAnalysis(pipeline, stepId) : null;
  const stepData = step
    ? [...step.inputStepDatas, ...step.outputStepDatas].find(({name}) => name === stepDataName)
    : undefined;

  /**
   * If there is step info, only allow step types which can be connected to `step` and `stepData`; otherwise,
   * only allow adding source steps.
   */
  const filterStepTypes = (stepType: PipelineStepType): boolean =>
    canAddStepType(stepType) &&
    (step && stepData ? canConnectStepTypeToStepData(step, stepData, stepType) : isSourceStep(stepType));

  // Add the new step type when Next is clicked
  const onNext = async (): Promise<void> => {
    if (!selectedStepType) {
      return;
    }
    const {stepId} = await withLoadingScreen(
      addStepWithInferredEdges(pipeline, {stepType: selectedStepType, stepData})
    );
    await withLoadingScreen(refreshAnalysis(pipeline.id));
    onChangeSelectedRightSidebarItem({type: AnalysisRightSidebarItemType.EDIT_STEP, stepId});
  };

  return (
    <RightSidebarSkeleton
      title="Add step"
      confirmationButtons={[
        {
          text: 'Cancel',
          onClick: () => {
            onChangeSelectedRightSidebarItem(null);
          },
          dataTestAttrsElement: 'cancel',
        },
        {
          text: 'Next',
          onClick: onNext,
          dataTestAttrsElement: 'next',
          disabled: !selectedStepType,
          isPrimary: true,
        },
      ]}
      width={RightSidebarWidth.MEDIUM}
      dataTestAttrs={{
        component: 'AddStepRightSidebar',
        element: 'root',
      }}
    >
      <div className={css(styles.container)}>
        <div className={css(styles.searchBar)}>
          <PlainSearchInput
            placeholder="Search..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
            }}
            onEscape={() => {
              setSearchQuery('');
            }}
            clearType={ClearType.X}
            borderRadius={BorderRadius.LARGE}
            className={css(styles.searchInput)}
          />
        </div>
        <div className={css(styles.menuWrapper)}>
          <StepTypeMenu
            filterStepTypes={filterStepTypes}
            selectedStepType={selectedStepType}
            onSelectStepType={setSelectedStepType}
            showDescription={showDescriptions}
            searchQuery={searchQuery}
          />
          <div className={css(styles.footer)}>
            <Switch
              label="Step descriptions"
              checked={showDescriptions}
              onChange={(e) => {
                setShowDescriptions(e.currentTarget.checked);
              }}
              styles={{
                root: {justifyContent: 'flex-end'},
                body: {alignItems: 'center'},
              }}
            />
          </div>
        </div>
      </div>
    </RightSidebarSkeleton>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  searchBar: {
    flexShrink: 0,
    paddingBottom: '12px',
  },
  searchInput: {
    width: '100%',
  },
  menuWrapper: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  footer: {
    flexShrink: 0,
    padding: '8px',
    display: 'flex',
    justifyContent: 'flex-end',
  },
});
